import { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AdminShopInquiry,
  getAdminShopInquiries,
  setAdminShopInquiryHandled,
} from '../../common/Rest/Services/AdminService/AdminService';
import ROUTES from '../../common/Routing/Routes';

const formatDate = (value: string) =>
  new Date(value).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const telLink = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

/** "Contact me" requests left on shop item pages. */
function AdminShopInquiries() {
  const [inquiries, setInquiries] = useState<AdminShopInquiry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showHandled, setShowHandled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await getAdminShopInquiries(showHandled ? undefined : false);
      setInquiries(page.items);
      setTotalCount(page.totalCount);
    } catch {
      setInquiries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [showHandled]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHandled = async (inquiry: AdminShopInquiry) => {
    setUpdatingId(inquiry.id);
    try {
      await setAdminShopInquiryHandled(inquiry.id, !inquiry.handledAt);
      await load();
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <section className="admin-shop-inquiries">
      <header>
        <h2>Запити «Уточнити деталі»{!showHandled && totalCount > 0 ? ` · ${totalCount} нових` : ''}</h2>
        <FormControlLabel
          control={<Switch checked={showHandled} onChange={(event) => setShowHandled(event.target.checked)} />}
          label="Показати оброблені"
        />
      </header>

      {loading ? (
        <CircularProgress size={24} />
      ) : inquiries.length === 0 ? (
        <p className="admin-empty">{showHandled ? 'Запитів ще не було.' : 'Нових запитів немає.'}</p>
      ) : (
        <div className="admin-shop-inquiry-list">
          {inquiries.map((inquiry) => (
            <article key={inquiry.id} className={inquiry.handledAt ? 'handled' : ''}>
              <div className="admin-shop-inquiry-main">
                <strong>{inquiry.name}</strong>
                <a href={telLink(inquiry.phone)}>{inquiry.phone}</a>
                <small>
                  <Link to={`${ROUTES.SHOP}/${inquiry.itemId}`} target="_blank" rel="noopener noreferrer">
                    {inquiry.itemName || `Товар #${inquiry.itemId}`}
                  </Link>
                  {' · '}
                  {formatDate(inquiry.createdAt)}
                </small>
                {inquiry.message && <p>{inquiry.message}</p>}
              </div>
              <Button
                size="small"
                variant={inquiry.handledAt ? 'text' : 'outlined'}
                onClick={() => toggleHandled(inquiry)}
                disabled={updatingId === inquiry.id}
              >
                {inquiry.handledAt ? 'Повернути в нові' : 'Оброблено'}
              </Button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default AdminShopInquiries;
